import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Modal, Popconfirm, Spin } from 'antd';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { deleteTherapyReservation } from '../../api/therapiesApi';
import { getTherapyReservationStatusLabel } from '../../utils/therapyPresentation';

const infoRowStyle = {
  display: 'grid',
  gridTemplateColumns: '160px 1fr',
  gap: 8,
  marginBottom: 10,
};

const labelStyle = {
  fontWeight: 600,
};

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// Period rezervacije: od meseca posle poslednjeg meseca aktuelne terapije
// (sidro = endDate / poslednji occurrence) pa count meseci unapred.
const getReservationPeriodLabel = (anchorDate, count) => {
  const anchor = anchorDate ? dayjs(anchorDate) : null;
  const monthsCount = Number(count) || 0;

  if (!anchor || !anchor.isValid() || monthsCount < 1) {
    return `${monthsCount || '-'} mes.`;
  }

  const start = anchor.add(1, 'month');
  const end = anchor.add(monthsCount, 'month');
  const formatMonth = (value) => capitalize(value.format('MMMM YYYY'));

  if (monthsCount === 1) {
    return `${formatMonth(start)} (1 mes.)`;
  }

  return `${formatMonth(start)} – ${formatMonth(end)} (${monthsCount} mes.)`;
};

const ViewTherapyReservationModal = ({
  isOpen,
  onClose,
  therapy,
  reservation,
  anchorDate,
  onEdit,
  onDeleted,
}) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const periodLabel = useMemo(
    () => getReservationPeriodLabel(anchorDate, reservation?.count),
    [anchorDate, reservation?.count]
  );

  const isPending = reservation?.status === 'pending';
  const canManage = isPending && Boolean(onEdit) && Boolean(onDeleted);

  const handleDelete = useCallback(async () => {
    if (!therapy?._id) {
      setError('Terapija nije izabrana.');
      return;
    }

    setDeleting(true);
    setError('');

    try {
      const { data, ok, message } = await deleteTherapyReservation(therapy._id);
      if (!ok) {
        setError(message);
        return;
      }

      await onDeleted?.(data);
      onClose?.();
    } finally {
      setDeleting(false);
    }
  }, [onClose, onDeleted, therapy?._id]);

  return (
    <Modal
      className="create_user_modal"
      title="Pogledaj rezervaciju unapred"
      open={isOpen}
      onCancel={deleting ? undefined : onClose}
      closable={!deleting}
      mask={{ closable: !deleting }}
      footer={
        canManage
          ? [
              <Button
                key="edit"
                type="primary"
                disabled={deleting}
                onClick={() => onEdit?.()}
              >
                Izmeni
              </Button>,
              <Popconfirm
                key="delete"
                title="Brisanje rezervacije"
                description="Da li ste sigurni da zelite da obrisete rezervaciju?"
                okText="Obrisi"
                cancelText="Odustani"
                okButtonProps={{ danger: true }}
                disabled={deleting}
                onConfirm={handleDelete}
              >
                <Button danger disabled={deleting} loading={deleting}>
                  Obrisi
                </Button>
              </Popconfirm>,
            ]
          : [
              <Button key="close" onClick={onClose}>
                Zatvori
              </Button>,
            ]
      }
    >
      <Spin spinning={deleting}>
        <div className="create_user_modal_form">
          <div style={infoRowStyle}>
            <span style={labelStyle}>Datum rezervacije:</span>
            <span>
              {reservation?.reservedAt
                ? dayjs(reservation.reservedAt).format('DD.MM.YYYY')
                : '-'}
            </span>
          </div>
          <div style={infoRowStyle}>
            <span style={labelStyle}>Status:</span>
            <span>{getTherapyReservationStatusLabel(reservation?.status)}</span>
          </div>
          <div style={infoRowStyle}>
            <span style={labelStyle}>Lek:</span>
            <span>{reservation?.medication || '-'}</span>
          </div>
          <div style={infoRowStyle}>
            <span style={labelStyle}>Period:</span>
            <span>{periodLabel}</span>
          </div>
          <div style={infoRowStyle}>
            <span style={labelStyle}>Beleske:</span>
            <span>{reservation?.note?.trim() || '-'}</span>
          </div>
          {error && (
            <Alert
              title={error}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      </Spin>
    </Modal>
  );
};

ViewTherapyReservationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  therapy: PropTypes.object,
  reservation: PropTypes.object,
  anchorDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onEdit: PropTypes.func,
  onDeleted: PropTypes.func,
};

export default ViewTherapyReservationModal;
